/**
 * Uber Menu Mappings (Uber Tools) — what goes on the Uber Eats menu and in which aisle.
 *
 * Jason, 25 Oct 2026: an aisle ("Uber menu name") holds every product in its Lightspeed
 * category PLUS every product carrying its Lightspeed tag — either may be blank. Straight
 * from Lightspeed, independent of the app's categories. Unmapped = not on Uber. Rows save as
 * they are changed.
 *
 * Since 26 Oct 2026 the Uber catalogue (Uber Menu page) holds what is on Uber; these rows name
 * and order the aisles, switch them on/off, and decide where a NEW Lightspeed product goes.
 * "#" = catalogue items in the aisle (before the catalogue exists: app-sellable products the
 * row matches). A full Lightspeed read takes ~30s, so the page never waits for it: while the
 * first one runs, the Lightspeed pickers are empty and the page asks again shortly.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';

const API = 'admin/tools/uber-menu/category-mappings';

interface Mapping {
  id: number;
  lightspeedCategory: string | null;
  lightspeedTag: string | null;
  uberMenuName: string;
  isActive: boolean;
  position: number;
  /** null while the first Lightspeed read is still running */
  productCount: number | null;
}
interface Named { name: string; count: number }
interface Draft { lightspeedCategory: string | null; lightspeedTag: string | null; uberMenuName: string }

const errorOf = (err: any, fallback: string) => err?.response?.data?.error || err?.message || fallback;

interface Props { onBack: () => void }

const UberMenuMappings: React.FC<Props> = ({ onBack }) => {
  const notify = useNotify();
  const [rows, setRows] = useState<Mapping[]>([]);
  const [categories, setCategories] = useState<Named[]>([]);
  const [tags, setTags] = useState<Named[]>([]);
  const [unmapped, setUnmapped] = useState<Named[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [lsNeverRead, setLsNeverRead] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      // only "Refresh from Lightspeed" waits for a full read (~30s)
      const { data } = await authInstance.get(API, refresh ? { params: { refresh: 'true' }, timeout: 120000 } : undefined);
      const d = data?.data ?? {};
      setRows(d.mappings ?? []);
      setCategories(d.lightspeedCategories ?? []);
      setTags(d.lightspeedTags ?? []);
      setUnmapped(d.unmappedCategories ?? []);
      setFetchedAt(d.catalogueFetchedAt ?? null);
      setLsNeverRead(!!d.lightspeedNeverRead);
      setError(null);
    } catch (err) {
      setError(errorOf(err, 'Could not load the mappings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);


  /** Save one row (PUT) with a change merged in; reload so "#" is current. */
  const save = async (row: Mapping, change: Partial<Mapping>) => {
    const next = { ...row, ...change };
    if (!next.lightspeedCategory && !next.lightspeedTag) {
      notify('A row needs a Lightspeed category, a Lightspeed tag, or both', { type: 'warning' });
      return;
    }
    if (!next.uberMenuName.trim()) {
      notify('The Uber menu name cannot be empty', { type: 'warning' });
      return;
    }
    setRows((rs) => rs.map((r) => (r.id === row.id ? next : r)));
    setSavingId(row.id);
    try {
      await authInstance.put(`${API}/${row.id}`, {
        uberMenuName: next.uberMenuName, lightspeedCategory: next.lightspeedCategory, lightspeedTag: next.lightspeedTag,
        isActive: next.isActive, position: next.position,
      });
      await load();
    } catch (err) {
      notify(errorOf(err, 'Could not save'), { type: 'error' });
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (row: Mapping) => {
    if (!window.confirm(`Remove "${row.uberMenuName}"? Its products leave the Uber menu at the next push.`)) return;
    try {
      await authInstance.delete(`${API}/${row.id}`);
      notify(`Removed ${row.uberMenuName}`, { type: 'success' });
      await load();
    } catch (err) {
      notify(errorOf(err, 'Could not remove'), { type: 'error' });
    }
  };

  const add = async () => {
    if (!draft) return;
    if (!draft.lightspeedCategory && !draft.lightspeedTag) {
      notify('Choose a Lightspeed category, a Lightspeed tag, or both', { type: 'warning' });
      return;
    }
    if (!draft.uberMenuName.trim()) {
      notify('Give the aisle an Uber menu name', { type: 'warning' });
      return;
    }
    try {
      const position = rows.reduce((m, r) => Math.max(m, r.position), 0) + 1;
      await authInstance.post(API, { ...draft, isActive: true, position });
      notify(`Added ${draft.uberMenuName}`, { type: 'success' });
      setDraft(null);
      await load();
    } catch (err) {
      notify(errorOf(err, 'Could not add'), { type: 'error' });
    }
  };

  const label = (list: Named[]) => (v: string) => {
    const hit = list.find((x) => x.name.toLowerCase() === v.toLowerCase());
    return hit ? `${hit.name} (${hit.count})` : v;
  };
  const picker = (list: Named[], value: string | null, onChange: (v: string | null) => void, aria: string, disabled = false) => (
    <Autocomplete
      size="small"
      options={list.map((x) => x.name)}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={label(list)}
      disabled={disabled}
      sx={{ minWidth: 190 }}
      renderInput={(params) => <TextField {...params} placeholder="None" inputProps={{ ...params.inputProps, 'aria-label': aria }} />}
    />
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={onBack} aria-label="Back"><BackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Uber Menu Mappings</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 6 }}>
        The aisles on Uber Eats, in order. Each takes the products in its Lightspeed category plus the products with its
        Lightspeed tag (either can be blank): that is where a new Lightspeed product goes when you add it in Push Uber Menu.
        What is on Uber now lives on the Uber Menu page; # counts its items in each aisle. An aisle switched off leaves
        Uber at the next push.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {lsNeverRead && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Lightspeed has not been read yet: click Refresh from Lightspeed to load the category and tag lists (about half a minute).
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDraft({ lightspeedCategory: null, lightspeedTag: null, uberMenuName: '' })} disabled={!!draft}>
          Add aisle
        </Button>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => load(true)} disabled={loading}>
          Refresh from Lightspeed
        </Button>
        {fetchedAt && (
          <Typography variant="caption" color="text.secondary">
            Lightspeed read {new Date(fetchedAt).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}
        {loading && <CircularProgress size={18} />}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>POS</TableCell>
              <TableCell>Uber menu name</TableCell>
              <TableCell>Lightspeed category</TableCell>
              <TableCell>Lightspeed tag</TableCell>
              <TableCell align="right"><Tooltip title="Items on the Uber menu in this aisle (from the Uber catalogue; before it is set up: Lightspeed products the row would take in)"><span>#</span></Tooltip></TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {draft && (
              <TableRow>
                <TableCell />
                <TableCell>
                  <TextField size="small" autoFocus placeholder="e.g. Braai & Party 🔥" value={draft.uberMenuName}
                    onChange={(e) => setDraft({ ...draft, uberMenuName: e.target.value })} inputProps={{ 'aria-label': 'New Uber menu name' }} />
                </TableCell>
                <TableCell>{picker(categories, draft.lightspeedCategory, (v) => setDraft({ ...draft, lightspeedCategory: v }), 'New Lightspeed category')}</TableCell>
                <TableCell>{picker(tags, draft.lightspeedTag, (v) => setDraft({ ...draft, lightspeedTag: v }), 'New Lightspeed tag')}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" variant="contained" onClick={add}>Save</Button>
                  <Button size="small" onClick={() => setDraft(null)}>Cancel</Button>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} sx={{ opacity: row.isActive ? 1 : 0.55 }}>
                <TableCell>
                  {/* key carries the saved value: a rejected or reverted save resets the box to it */}
                  <TextField key={`pos-${row.position}`} size="small" type="number" defaultValue={row.position} sx={{ width: 70 }}
                    inputProps={{ 'aria-label': `Position of ${row.uberMenuName}`, min: 0 }}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!Number.isFinite(v)) { e.target.value = String(row.position); return; }
                      if (v !== row.position) save(row, { position: v });
                    }} />
                </TableCell>
                <TableCell>
                  <TextField key={`name-${row.uberMenuName}`} size="small" defaultValue={row.uberMenuName} fullWidth
                    inputProps={{ 'aria-label': `Uber menu name of ${row.uberMenuName}` }}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (!v) { e.target.value = row.uberMenuName; notify('The Uber menu name cannot be empty', { type: 'warning' }); return; }
                      if (v !== row.uberMenuName) save(row, { uberMenuName: v });
                    }} />
                </TableCell>
                <TableCell>{picker(categories, row.lightspeedCategory, (v) => save(row, { lightspeedCategory: v }), `Lightspeed category of ${row.uberMenuName}`, savingId === row.id)}</TableCell>
                <TableCell>{picker(tags, row.lightspeedTag, (v) => save(row, { lightspeedTag: v }), `Lightspeed tag of ${row.uberMenuName}`, savingId === row.id)}</TableCell>
                {/* green when the aisle has items, as Command Center's Uber Menu Mappings shows it (Jason, 26 Oct 2026) */}
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: (row.productCount ?? 0) > 0 ? 'success.main' : 'text.secondary' }}>{row.productCount ?? '—'}</TableCell>
                <TableCell align="center">
                  <Switch checked={row.isActive} disabled={savingId === row.id} onChange={(e) => save(row, { isActive: e.target.checked })}
                    slotProps={{ input: { 'aria-label': `${row.uberMenuName} active` } }} />
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove this aisle">
                    <IconButton size="small" onClick={() => remove(row)} aria-label={`Remove ${row.uberMenuName}`}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && !draft && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No aisles yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {unmapped.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Lightspeed categories not on Uber</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Not mapped, so not on the Uber menu. Click one to start an aisle for it.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {unmapped.map((c) => (
              <Chip key={c.name} label={`${c.name} (${c.count})`} variant="outlined" size="small"
                onClick={() => setDraft({ lightspeedCategory: c.name, lightspeedTag: null, uberMenuName: c.name })} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UberMenuMappings;
